import {useCallback, useRef} from 'react';
import {trackEvent} from '@src/Tracking/trackEvent';
import {TraceTokEvents} from '@src/Tracking/catalog';

type MediaContent = {id?: string | null; typename?: string};

interface UseMediaPlayerTrackingOptions {
  // player_type dimension carried on content_started.
  playerType: string;
  // Live stream (e.g. radio): no fixed duration, so content_completed and
  // percentage-based watch_progress do not apply. For live players
  // content_started is emitted on the first play instead of on the first
  // progression tick (they usually do not surface a timeupdate at the page).
  live?: boolean;
}

/**
 * Shared player analytics for the audio/quiz players that previously emitted no
 * playback events. It reproduces the News (vertical) player's proven, event
 * driven logic so every player emits the same five content_* events:
 *   content_started, watch_progress (60s heartbeat), content_completed,
 *   content_paused, content_resumed.
 *
 * Emits through the module bridge (trackEvent), which the provider registers on
 * window, so it works even inside lazy-loaded route chunks where the
 * useTracking() hook resolves to a silent noop.
 *
 * The content identity ({id, typename}) and the total duration are passed in at
 * call time so the hook never holds a stale closure over the player's state.
 */
export function useMediaPlayerTracking({
  playerType,
  live = false,
}: UseMediaPlayerTrackingOptions) {
  const hasStartedRef = useRef(false); // content_started emitted for current content
  const startedIdRef = useRef<string | null>(null); // which content id we started
  const wasPausedRef = useRef(false); // a genuine pause happened => next play is a resume
  const isEndingRef = useRef(false); // the media is ending (ignore the trailing pause)
  const watchTimeRef = useRef(0); // accumulated real seconds watched
  const lastTimeRef = useRef(0); // last playback position seen (to compute delta)
  const lastHeartbeatRef = useRef(0); // watch seconds at the last 60s heartbeat
  const completedRef = useRef(false); // content_completed already fired for current content

  const reset = useCallback(() => {
    hasStartedRef.current = false;
    startedIdRef.current = null;
    wasPausedRef.current = false;
    isEndingRef.current = false;
    watchTimeRef.current = 0;
    lastTimeRef.current = 0;
    lastHeartbeatRef.current = 0;
    completedRef.current = false;
  }, []);

  const emitStarted = useCallback(
    (content: MediaContent) => {
      hasStartedRef.current = true;
      startedIdRef.current = content.id ?? null;
      isEndingRef.current = false;
      wasPausedRef.current = false;
      watchTimeRef.current = 0;
      lastHeartbeatRef.current = 0;
      completedRef.current = false;
      trackEvent(TraceTokEvents.ContentStarted, {
        content_id: content.id,
        content_type: content.typename,
        player_type: playerType,
        start_threshold: 0,
      });
    },
    [playerType],
  );

  // Call on every playback position update (seconds). Drives content_started,
  // the 60s watch_progress heartbeat, and the 95% content_completed milestone.
  const onProgress = useCallback(
    (seconds: number, content: MediaContent, durationSec: number) => {
      const delta = seconds - lastTimeRef.current;
      lastTimeRef.current = seconds;
      // Ignore seeks, paused gaps and the initial jump.
      if (!(delta > 0 && delta < 2)) return;

      const id = content.id ?? null;
      // First real progression on this content => content_started (reliable:
      // timeupdate only fires while the media is truly playing).
      if (!hasStartedRef.current || startedIdRef.current !== id) {
        emitStarted(content);
      }

      watchTimeRef.current += delta;

      // watch_progress — a 60s heartbeat.
      if (
        Math.floor(watchTimeRef.current / 60) >
        Math.floor(lastHeartbeatRef.current / 60)
      ) {
        lastHeartbeatRef.current = watchTimeRef.current;
        trackEvent(TraceTokEvents.WatchProgress, {
          content_id: content.id,
          content_type: content.typename,
          progression:
            !live && durationSec > 0 ? watchTimeRef.current / durationSec : 0,
          seconds_watched: Math.round(watchTimeRef.current),
          heartbeat_time: 60,
        });
      }

      // content_completed at the 95% completion point (by playback position),
      // once per content. Not applicable to live streams.
      if (
        !live &&
        !completedRef.current &&
        durationSec > 0 &&
        (seconds / durationSec) * 100 >= 95
      ) {
        completedRef.current = true;
        trackEvent(TraceTokEvents.ContentCompleted, {
          content_id: content.id,
          content_type: content.typename,
          completion_threshold: Math.round((seconds / durationSec) * 100),
          watch_sec: Math.round(watchTimeRef.current),
        });
      }
    },
    [live, emitStarted],
  );

  // Call when the media reaches its natural end. Fallback content_completed for
  // content that ends without a timeupdate landing at/after 95%.
  const onEnd = useCallback(
    (content: MediaContent, durationSec: number) => {
      isEndingRef.current = true;
      if (!live && !completedRef.current) {
        completedRef.current = true;
        trackEvent(TraceTokEvents.ContentCompleted, {
          content_id: content.id,
          content_type: content.typename,
          completion_threshold:
            durationSec > 0
              ? Math.round((watchTimeRef.current / durationSec) * 100)
              : undefined,
          watch_sec: Math.round(watchTimeRef.current),
        });
      }
      reset();
    },
    [live, reset],
  );

  // Call on play/pause. isPaused=true => content_paused; isPaused=false after a
  // genuine pause => content_resumed. For live players the first play emits
  // content_started (there is no progression tick to trigger it).
  const onPlayPause = useCallback(
    (isPaused: boolean, content: MediaContent) => {
      if (isPaused) {
        if (!hasStartedRef.current) return;
        if (isEndingRef.current) {
          isEndingRef.current = false;
          return;
        }
        wasPausedRef.current = true;
        trackEvent(TraceTokEvents.ContentPaused, {content_id: content.id});
        return;
      }
      // play / resume
      if (!hasStartedRef.current) {
        // Live players have no progression tick, so start here. VOD-style
        // players start from onProgress, so do not double-emit.
        if (live) emitStarted(content);
        return;
      }
      if (isEndingRef.current) {
        isEndingRef.current = false;
        return;
      }
      if (!wasPausedRef.current) return; // not a genuine resume
      wasPausedRef.current = false;
      trackEvent(TraceTokEvents.ContentResumed, {content_id: content.id});
    },
    [live, emitStarted],
  );

  return {onProgress, onEnd, onPlayPause, reset};
}
